import {BaseComponent} from './base-component.js'
import {customElement, property} from 'lit/decorators.js'
import {classMap} from 'lit-html/directives/class-map.js'
import {html, css, TemplateResult, CSSResultGroup} from 'lit'

@customElement('il-product-card-deprecated')
export default class ProductCardComponent extends BaseComponent {
  @property({type: String})
  heading: string = ''

  @property({attribute: 'sub-heading', type: String})
  subHeading: string = ''

  @property({attribute: 'price-pre-text', type: String})
  pricePreText: string = ''

  @property({attribute: 'price-text', type: String})
  priceText: string = ''

  @property({attribute: 'price-post-text', type: String})
  pricePostText: string = ''

  @property({attribute: 'reviews-text', type: String})
  reviewsText: string = ''

  @property({type: Array})
  images: string[] = []

  @property({attribute: 'sold-out-text', type: String})
  soldOutText: string = 'Sold Out'

  @property({attribute: 'is-sold-out', type: Boolean})
  isSoldOut: boolean = false

  @property({attribute: 'is-selected', type: Boolean})
  isSelected: boolean = false

  @property({attribute: 'image-height', type: Number})
  imageHeight: number = 200

  @property({attribute: 'fill-height', type: Boolean, reflect: true})
  fillHeight: boolean = false

  @property({type: Object})
  onClick?: () => void

  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      :host([fill-height]) {
        height: 100%;
      }

      .il-product-card {
        --il-product-card-width: 100%;
        --il-product-card-min-width: 200px;
        --il-product-card-padding: 1rem;
        --il-product-card-image-height: 236px;
        --il-product-card-image-opacity: 1;

        background-color: var(--il-product-card-background-color);
        width: var(--il-product-card-width);
        min-width: var(--il-product-card-min-width);

        border-radius: 0.75rem;
        background: var(--il-color-neutrals-0);
        box-shadow: var(--il-shadow-cool-raised);

        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      .il-product-card.il-product-card--selected {
        border: 2px solid var(--il-color-accent-100);
      }

      .il-product-card.il-product-card--sold-out {
        --il-product-card-image-opacity: 0.5;
      }

      .il-product-card.il-product-card--clickable {
        position: relative;
      }

      .il-product-card.il-product-card--clickable:hover .il-product-card__image {
        background-color: black;
      }

      .il-product-card.il-product-card--clickable:hover .il-product-card__image img {
        opacity: 0.9;
      }

      .il-product-card.il-product-card--fill-height {
        height: 100%;
      }


      .il-product-card .il-product-card__image {
        position: relative;
        width: 100%;
        height: auto;
        aspect-ratio: 3 / 2;
        overflow: hidden;
      }

      .il-product-card .il-product-card__image img {
        opacity: var(--il-product-card-image-opacity);
        object-fit: cover;
        object-position: center;
        aspect-ratio: 3 / 2;
        height: 100%;
      }

      .il-product-card .il-product-card__sold-out {
        padding: 0.25rem 0.5rem;
        position: absolute;
        left: 1rem;
        bottom: 1rem;
        border-radius: 0.25rem;
        border: 1px solid var(--il-color-neutrals-100);
        background: var(--il-color-neutrals-50);
        color: var(--il-color-neutrals-350);
        font-size: var(--il-font-size-16);
        font-weight: var(--il-font-weight-bold);
        line-height: var(--il-font-line-height-125);
        letter-spacing: var(--il-font-letter-spacing-2);
      }

      .il-product-card .il-product-card__content {
        padding: var(--il-product-card-padding);
        display: flex;
        align-items: center;
        gap: 1rem;
      }

      .il-product-card.il-product-card--fill-height .il-product-card__content {
        margin: auto 0;
      }

      .il-product-card .il-product-card__button {
        position: absolute;
        inset: 0;
        opacity: 0;
        cursor: pointer;
      }

      .il-product-card__main {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        flex: 1 1 auto;
      }

      .il-product-card__main .il-product-card__heading {
        color: var(--il-color-neutrals-350);
        font-size: var(--il-font-size-16);
        font-weight: var(--il-font-weight-xbold);
        line-height: var(--il-font-line-height-125);
        letter-spacing: var(--il-font-letter-spacing-2);
      }

      .il-product-card__main .il-product-card__sub-heading {
        color: var(--il-color-neutrals-300);
        font-size: var(--il-font-size-14);
        font-weight: var(--il-font-weight-normal);
        line-height: var(--il-font-line-height-150);
        letter-spacing: var(--il-font-letter-spacing-2);
      }

      .il-product-card__main .il-product-card__extra {
        display: flex;
        gap: 0.5rem;
      }

      .il-product-card__main .il-product-card__reviews {
        padding: 1px 6px;
        border-radius: 0.25rem;
        border: 1px solid var(--il-color-neutrals-100);
        background: var(--il-color-neutrals-50);
        display: flex;
        gap: 1rem;

        color: var(--il-color-neutrals-300);
        font-size: var(--il-font-size-14);
        font-weight: var(--il-font-weight-bold);
        line-height: var(--il-font-line-height-140);
        letter-spacing: var(--il-font-letter-spacing-2);
      }
    `,
  ]

  get hasImages (): boolean {
    return !!this.images?.length
  }

  get mainImage (): string {
    return this.images?.[0]
  }

  get hasOnClick (): boolean {
    return this.onClick !== undefined && this.onClick !== null
  }

  _renderSoldOut (): TemplateResult {
    if (this.isSoldOut) {
      return html`
        <div
          class="il-product-card__sold-out"
          data-test-iliad-product-card-sold-out
        >
          ${this.soldOutText}
        </div>
      `
    }

    return html``
  }

  // To be completed in the future, this should be a carousel of images but specs are not ready
  _renderImage (): TemplateResult {
    if (this.hasImages) {
      return html`
        <div class="il-product-card__image" data-test-iliad-product-card-image>
          <img src=${this.mainImage} alt=${this.heading} />
          ${this._renderSoldOut()}
        </div>
      `
    }

    return html``
  }

  _renderSubHeading (): TemplateResult {
    if (this.subHeading) {
      return html`
        <div
          class="il-product-card__sub-heading"
          data-test-iliad-product-card-sub-heading
        >
          ${this.subHeading}
        </div>
      `
    }

    return html``
  }

  _renderExtra (): TemplateResult {
    if (this.priceText || this.reviewsText) {
      return html`
        <div class="il-product-card__extra" data-test-iliad-product-card-extra>
          ${this._renderPrice()} ${this._renderReviews()}
        </div>
      `
    }

    return html``
  }

  _renderPrice (): TemplateResult {
    if (this.priceText) {
      return html`
        <il-price-tag data-test-iliad-product-card-price>
          ${this.pricePreText &&
          html`<span part="small">${this.pricePreText}</span>`}
          ${this.priceText}
          ${this.pricePostText &&
          html`<span part="small">${this.pricePostText}</span>`}
        </il-price-tag>
      `
    }

    return html``
  }

  _renderReviews (): TemplateResult {
    if (this.reviewsText) {
      return html`
        <div
          class="il-product-card__reviews"
          data-test-iliad-product-card-reviews
        >
          ${this.reviewsText}
        </div>
      `
    }

    return html``
  }

  _renderOnClickButton (): TemplateResult {
    if (this.hasOnClick) {
      return html`
        <button
          class="il-product-card__button"
          @click="${() => this._onClick()}"
          data-test-iliad-product-card-button
        ></button>
      `
    }

    return html``
  }

  _onClick (): void {
    this.onClick?.()
  }

  private get cardClasses (): {[x: string]: boolean} {
    return {
      [`${this.baseCSSClass} il-product-card`]: true,
      'il-product-card--selected': this.isSelected,
      'il-product-card--sold-out': this.isSoldOut,
      'il-product-card--clickable': this.hasOnClick,
      'il-product-card--fill-height': this.fillHeight,
    }
  }

  override render (): TemplateResult {
    return html`
      <div class="${classMap(this.cardClasses)}" data-test-iliad-product-card>
        ${this._renderImage()}
        <div
          class="il-product-card__content"
          data-test-iliad-product-card-content
        >
          <div class="il-product-card__main" data-test-iliad-product-card-main>
            <div
              class="il-product-card__heading"
              data-test-iliad-product-card-heading
            >
              ${this.heading}
            </div>

            ${this._renderSubHeading()} ${this._renderExtra()}
          </div>
          ${this._renderOnClickButton()}
          <slot class="il-product-card__secondary"></slot>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'il-product-card-deprecated': ProductCardComponent
  }
}
